import * as React from 'react';
import { useEffect, useState } from 'react';

import type { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import redirect, { setLocation } from 'js/lib/coursera.redirect';
import store from 'js/lib/coursera.store';
import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import type { AdsManagerAction, ProductType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { HAS_PREVIOUSLY_LOGGED_IN } from 'bundles/authentication-common/constants';
import useIsInLegalConsolidationFlowAndHasTouQueryParam from 'bundles/authentication-common/hooks/useIsInLegalConsolidationFlowAndHasTouQueryParam';
import GetS12nCertificateModal from 'bundles/enroll-common/components/common/GetS12nCertificateModal';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { ENROLL } from 'bundles/enroll-common/utils/enrollActionParams';
import {
  canEnrollThroughFreemium,
  enrollThroughFreemium,
  isFreemiumModelRolloutGAEnabled,
} from 'bundles/enroll-common/utils/freemiumModelUtils';
import {
  generateImpressionForUsPricingTest,
  isEligibleCourseForUsPricingTest,
} from 'bundles/enroll-common/utils/usPricingTestUtils';
import EnrollModal from 'bundles/enroll-modal/components/EnrollModal';
import PreloadEnrollModal from 'bundles/enroll-modal/components/PreloadEnrollModal';
import { MODAL_TYPES, useEnrollmentModalState } from 'bundles/enroll-modal/hooks/useEnrollmentModalState';
import { getProductCategory } from 'bundles/enroll-modal/utils/gtmUtils';
import { getCourseHomeLinkBySlug } from 'bundles/enterprise-legacy-learner-home/utils/courseAndS12nUtils';
import type { CourseType } from 'bundles/enterprise-legacy-learner-home/utils/courseTypeMetadataUtils';
import paymentsExperiments from 'bundles/epic/clients/payments';
import adsTracker from 'bundles/event-tracking/lib';
import OpenAidApplicationModal, {
  MODAL_TYPES as OPEN_AID_MODAL_TYPES,
} from 'bundles/finaid-common/components/OpenAidApplicationModal';
import OpenAidApplicationModalV2 from 'bundles/finaid-common/components/OpenAidApplicationModalV2';
import type { FinancialAidApplicationsWithUserAndProducts_FinancialAidApplicationsV2Resource_byUserAndProductIds_elements as FinancialAidApplication } from 'bundles/finaid-common/enhancers/__generated__/FinancialAidApplicationsWithUserAndProducts';
import type { CourseTypeMetadataV1 } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import type PromotionEligibilitiesV1 from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import type S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import { generateCharmPricingInUsTestImpression } from 'bundles/ownable-products-common/utils/generateCharmPricingInUsTestImpression';
import { useGenerateEnrollModalImpressionForHighIncomeCountryPricingTest } from 'bundles/payments-common/utils/highIncomeCountryPricingTestUtils';
import { generateImpressionForBillingEngineDlocalAB } from 'bundles/payments-common/utils/stripeUtils';
import TopLevelModal from 'bundles/phoenix/components/TopLevelModal';
import { ProfessionalCertificateS12n } from 'bundles/s12n-common/constants/s12nProductVariants';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';

type OpenFinAidInfo = Partial<FinancialAidApplication> & {
  courseName: string;
  courseId: string;
};

export type PropsFromCaller = {
  courseId: string;
  course?: CoursesV1;
  isEnrolled: boolean;
  isSpecialization?: boolean;
  isEligibleForPlusMonthlyToAnnualUpgrade: boolean;
  isCourseraPlusEligibleProduct?: boolean;
  showUndoCancellationCTA?: boolean;
  isS12nUpgradeEligible?: boolean;
  s12nId?: string;
  currentS12nId?: string;
  name?: string;
  slug?: string;
  courseType?: CourseType;
  s12nProductVariant?: DescriptionPageProductVariant | S12nProductVariant;
  hideComponent?: boolean;
  additionalQueryParams?: { [key: string]: string };
  shouldCheckActionParams?: boolean;
  shouldCheckProductIdParam?: boolean;
  enrollmentAvailableChoices?: EnrollmentChoices;
  s12nDerivative?: S12nDerivativesV1;
  promotionEligibilities?: PromotionEligibilitiesV1;
  router: InjectedRouter;
  trackingName?: string;
  rootClassName?: string;
  alternateEnrollFlow?: () => boolean; // return `true` to skip normal enroll flow
  ctaBottom?: string | JSX.Element;
  showOpenAidModal?: boolean;
  openFinAidInfo?: OpenFinAidInfo;
  productVariant?: string;
  subtitleElem?: React.ReactNode;
  onClick?: () => void;
  buttonTrackingName?: string;
  partnerIds?: Array<string>;
  lastActiveCourseSlug?: string;
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1;
};

type ExtraProps = {
  onClick: () => void;
  onMultipleLearningProgramsLinkClick?: () => void;
};

function withEnrollModal<Props extends PropsFromCaller>(
  WrappedComponent: React.ComponentType<Props & ExtraProps>
): React.ComponentType<Props> {
  const WithEnrollModal = (props: Props) => {
    const {
      isEnrolled,
      slug,
      router,
      additionalQueryParams,
      shouldCheckActionParams,
      shouldCheckProductIdParam,
      enrollmentAvailableChoices,
      courseType,
      s12nProductVariant,
      name,
      courseId,
      course,
      s12nId,
      currentS12nId,
      isS12nUpgradeEligible = false,
      trackingName,
      isSpecialization,
      alternateEnrollFlow,
      showOpenAidModal,
      productVariant,
      openFinAidInfo,
      hideComponent,
      rootClassName,
      ctaBottom,
      partnerIds,
      lastActiveCourseSlug,
      courseTypeMetadataWithVersion,
    } = props;

    const retrackedTrack = useRetracked();
    const track = useTracker();
    const {
      activeModal,
      toggleOpenAidModal,
      toggleEnrollModal,
      toggleGetS12nCertificateModal,
      fromMultipleLearningProgramsLink,
      setFromMultipleLearningProgramsLink,
    } = useEnrollmentModalState();
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const isLegalConsolidationFlowAndHasTouQueryParam = useIsInLegalConsolidationFlowAndHasTouQueryParam();
    const canEnrollThroughPreview =
      enrollmentAvailableChoices &&
      courseTypeMetadataWithVersion?.courseTypeMetadata &&
      canEnrollThroughFreemium({
        courseId,
        enrollmentAvailableChoices,
        courseTypeMetadataWithVersion,
      });
    const isAnAuthenticatedUser = user.isAuthenticatedUser();

    useGenerateEnrollModalImpressionForHighIncomeCountryPricingTest({
      enrollmentAvailableChoices,
      courseId,
      s12nId,
      skip: activeModal !== MODAL_TYPES.ENROLL, // only generate impression upon opening the enroll modal
    });

    const getGTMTrackingData = () => {
      const productCategory = getProductCategory({
        courseType,
        s12nProductVariant: isSpecialization ? s12nProductVariant : undefined,
      });
      const data = {
        userId: user.get().id,
        productCategory,
        productName: name,
        productSlug: router.params?.slug || router.params?.productSlug,
        productId: isSpecialization ? s12nId : courseId,
      };
      return data;
    };
    const getProductType = () => {
      let productType: ProductType | undefined;

      if (isSpecialization) {
        productType = 's12n';
      } else if (courseType === 'GuidedProject') {
        productType = 'guided_project';
      } else if (courseType === 'Project') {
        productType = 'project';
      } else {
        productType = 'course';
      }

      return productType;
    };

    const trackGTMForEventingV3 = (adsManagerAction: AdsManagerAction, productId?: string) => {
      if (!productId) {
        return;
      }

      track('ads_tracker_google_tag_manager', {
        adsManagerAction,
        product: {
          id: productId,
          type: getProductType(),
        },
      });
    };

    const handleClickPreview = () => {
      if (canEnrollThroughPreview) {
        setIsPreviewLoading(true);
        enrollThroughFreemium(courseId)
          .then(() => {
            setIsPreviewLoading(false);
            const courseSlug = course?.slug;
            if (courseSlug) {
              return setLocation(`/learn/${courseSlug}/home/welcome`);
            }
            return setLocation('/');
          })
          .catch(() => {
            setIsPreviewLoading(false);
          });
      } else {
        toggleEnrollModal();
      }
    };

    const trackEnroll = () => {
      // Introduce timeout so that impressions can fire before the tracking
      setTimeout(() => {
        const data = getGTMTrackingData();

        trackGTMForEventingV3('enroll', data.productId);
        adsTracker.trackEnroll(data);
      }, 100);
    };

    const trackEnrollButtonClick = () => {
      // Introduce timeout so that impressions can fire before the tracking
      setTimeout(() => {
        const data = getGTMTrackingData();

        trackGTMForEventingV3('enroll_button_click', data.productId);
        adsTracker.trackEnrollButtonClick(data);
      }, 100);
    };

    const showEnrollmentChoice = () => {
      // Three possibilities here:
      //  1. alternateEnrollFlow doesn't exist: the call "returns" undefined and we continue with showEnrollmentChoice
      //  2. alternateEnrollFlow exists and returns false: alternateEnrollFlow is called and we continue with
      //     showEnrollmentChoice
      //  3. alternateEnrollFlow exists and returns true: alternateEnrollFlow is called and we SKIP
      //     showEnrollmentChoice (alternateEnrollFlow replaces the normal enroll flow)
      // Example use-case for #3 would be to scroll the page to the Enrollment Options section INSTEAD OF showing the
      // Enroll modal.
      if (showOpenAidModal) {
        toggleOpenAidModal();
        return;
      }

      if (alternateEnrollFlow?.()) {
        return;
      }

      toggleEnrollModal();
    };

    const handleNormalEnrollmentFlow = () => {
      const hasEarnedS12nCert = enrollmentAvailableChoices?.hasEarnedS12nCertificate;
      const preEnrollEnabledButNotEligible = isSpecialization
        ? enrollmentAvailableChoices?.isNotEligibleToPreEnroll
        : enrollmentAvailableChoices?.isNotEligibleToPreEnroll &&
          enrollmentAvailableChoices?.hasOnlyPreEnrollEnabledS12ns;

      if (!isAnAuthenticatedUser) {
        const hasPreviouslyLoggedIn = store.get(HAS_PREVIOUSLY_LOGGED_IN);

        const authModeValue = hasPreviouslyLoggedIn ? 'login' : 'signup';
        router.replace({
          pathname: router.location.pathname,
          query: omit(
            {
              ...router.location.query,
              ...additionalQueryParams,
              action: ENROLL,
              ...(shouldCheckProductIdParam ? { productId: isSpecialization ? s12nId : courseId } : {}),
            },
            'authMode'
          ),
          hash: buildHashWithAuthMode(getLocationHash(router.location), authModeValue),
        });

        if (hasPreviouslyLoggedIn) {
          retrackedTrack({
            trackingName: HAS_PREVIOUSLY_LOGGED_IN,
            action: 'show',
            trackingData: {},
          });
        }
      } else if (isSpecialization && course && isS12nUpgradeEligible) {
        // For users on SDP and enrolled in an older version, upgrading should take priority over switching subscriptions.
        // The primary button will be "Go to course"
        redirect.setLocation(getCourseHomeLinkBySlug(lastActiveCourseSlug ?? course.slug));
      } else if (isEnrolled && isSpecialization && course && !course.isPreEnroll) {
        redirect.setLocation(getCourseHomeLinkBySlug(course.slug));
      } else if (isEnrolled && typeof slug === 'string') {
        redirect.setLocation(getCourseHomeLinkBySlug(slug));
      } else if (hasEarnedS12nCert) {
        toggleGetS12nCertificateModal();
      } else if (preEnrollEnabledButNotEligible) {
        // If this is the XDP for something that is pre enroll enabled but not eligible to pre-enroll, we shouldn't open
        // any modals and should thus do nothing. This can be removed once pre-enrollment is fully rolled out
      } else {
        trackEnroll();
        // Generate impression for [Q1 2024] Charm Pricing for s12ns and courses in US
        // Link to epic: https://tools.coursera.org/epic/experiment/_4x3VeCCEe6K0goRikKsMw
        const productType = getProductType();
        if (
          (productType === 'course' || productType === 's12n') &&
          !enrollmentAvailableChoices?.canEnrollThroughCourseraPlus
        ) {
          generateCharmPricingInUsTestImpression(partnerIds);
        }

        showEnrollmentChoice(); // null to reset enrollmentChoice override
      }
    };

    const _onClick = () => {
      const trackingData = {
        s12nId,
        courseId,
        path: window.location.pathname,
        isAuthenticatedUser: isAnAuthenticatedUser,
      };

      if (trackingName) {
        retrackedTrack({
          trackingName,
          action: 'click',
          trackingData,
        });
      }

      handleNormalEnrollmentFlow();
    };

    const onClick = () => {
      // We want to track all enroll button clicks, even for logged out users
      trackEnrollButtonClick();

      // For all other clicks, we should make sure that the openS12nSelectModal is set to false
      setFromMultipleLearningProgramsLink(false);
      _onClick();
    };

    const onMultipleLearningProgramsLinkClick = () => {
      // This is specifically used for pre enrollment when the multiple learning programs link
      // is clicked that we open up specifically the s12n select modal. The link only
      // shows up for mix-and-match cases and should always open up the s12n select modal
      setFromMultipleLearningProgramsLink(true);
      _onClick();
    };

    useEffect(() => {
      const { action, productId } = router.location.query;

      const hasValidProductIdParam =
        shouldCheckProductIdParam &&
        ((isSpecialization && s12nId === productId) || (!isSpecialization && courseId === productId));
      const hasValidEnrollQueryParams = (hasValidProductIdParam || shouldCheckActionParams) && action === ENROLL;

      if (!isEnrolled && !isLegalConsolidationFlowAndHasTouQueryParam && hasValidEnrollQueryParams) {
        onClick();
      }

      if (enrollmentAvailableChoices?.canPreEnroll || enrollmentAvailableChoices?.isPreEnrolled) {
        // Generate impression for pre-enrollment V2 experiment
        // Link to epic: https://tools.coursera.org/epic/experiment/b2P1rzuOEe2tJw5DoCIqiQ
        paymentsExperiments.get('disablePreEnrollment');
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLegalConsolidationFlowAndHasTouQueryParam]);

    useEffect(() => {
      if (
        activeModal === MODAL_TYPES.ENROLL &&
        isEligibleCourseForUsPricingTest({
          isSpecialization,
          courseTypeMetadataWithVersion,
          enrollmentAvailableChoices,
        })
      ) {
        generateImpressionForUsPricingTest();
      }

      if (
        activeModal === MODAL_TYPES.ENROLL &&
        isAnAuthenticatedUser &&
        enrollmentAvailableChoices?.canSubscribeToS12n
      ) {
        generateImpressionForBillingEngineDlocalAB();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeModal]);

    const getOpenAidAppModalType = () => {
      if (productVariant === ProfessionalCertificateS12n) {
        return OPEN_AID_MODAL_TYPES.PROFESSIONAL_CERTIFICATE;
      } else if (isSpecialization && s12nId) {
        return OPEN_AID_MODAL_TYPES.SPECIALIZATION;
      }
      return OPEN_AID_MODAL_TYPES.COURSE;
    };

    const enrollModalProps =
      isSpecialization && s12nId
        ? { s12nId, courseId, partnerIds, onSdp: true, courseTypeMetadataWithVersion }
        : { s12nId, courseId, partnerIds, fromMultipleLearningProgramsLink, courseTypeMetadataWithVersion };

    const renderModal = () => {
      const isFreemiumGAEnabled = isFreemiumModelRolloutGAEnabled();
      switch (activeModal) {
        case MODAL_TYPES.ENROLL:
          return (
            <TopLevelModal>
              <EnrollModal {...enrollModalProps} router={router} onClose={toggleEnrollModal} />
            </TopLevelModal>
          );
        case MODAL_TYPES.GET_CERTIFICATE:
          if (s12nId) {
            return <GetS12nCertificateModal s12nId={s12nId} onClose={toggleGetS12nCertificateModal} />;
          } else {
            throw new Error('Expect MODAL_TYPES.GET_CERTIFICATE to have a valid s12nId');
          }
        case MODAL_TYPES.OPEN_AID:
          return isFreemiumGAEnabled ? (
            <OpenAidApplicationModalV2
              type={getOpenAidAppModalType()}
              approvalDate={openFinAidInfo?.approvalDate}
              cartId={openFinAidInfo?.cartId}
              applicationState={openFinAidInfo?.state}
              handleClose={toggleOpenAidModal}
              handlePreview={handleClickPreview}
              isPreviewLoading={isPreviewLoading}
              isPreviewAvailable={canEnrollThroughPreview}
              handleCancelAndEnroll={toggleEnrollModal}
            />
          ) : (
            <OpenAidApplicationModal
              type={getOpenAidAppModalType()}
              courseName={openFinAidInfo?.courseName}
              approvalDate={openFinAidInfo?.approvalDate}
              cartId={openFinAidInfo?.cartId}
              applicationState={openFinAidInfo?.state}
              handleClose={toggleOpenAidModal}
              s12nId={s12nId}
              handleCancelAndEroll={toggleEnrollModal}
            />
          );
        default:
          return null;
      }
    };

    if (hideComponent) {
      return null;
    }

    const s12nUpgradeLink = isS12nUpgradeEligible ? `/learn/specialization/upgrade/${currentS12nId}:${s12nId}` : '';

    return (
      <div className={classNames('with-enroll-modal', rootClassName)}>
        {renderModal()}
        {paymentsExperiments.get('preloadEnrollModalEnabled') ? <PreloadEnrollModal {...enrollModalProps} /> : null}
        <WrappedComponent
          {...props}
          ctaBottom={ctaBottom}
          onClick={onClick}
          onMultipleLearningProgramsLinkClick={onMultipleLearningProgramsLinkClick}
          s12nUpgradeLink={s12nUpgradeLink}
        />
      </div>
    );
  };

  return WithEnrollModal;
}

export default withEnrollModal;
